import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Download, ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function UserManualTable({ officers }) {
  const [isOpen, setIsOpen] = useState(false);
  // Flatten user manual data into rows: officer, email, question, answer
  const flattenUserManuals = () => {
    const rows = [];
    
    officers.forEach((officer) => {
      if (!officer.user_manual) return;
      
      const sections = [
        { title: 'alignment', label: 'Alignment' },
        { title: 'value', label: 'Value' },
        { title: 'communication', label: 'Communication' },
        { title: 'decisions', label: 'Decisions' },
        { title: 'conflict', label: 'Conflict' },
        { title: 'energy', label: 'Energy' },
        { title: 'improvement', label: 'Improvement' }
      ];
      
      sections.forEach(({ title, label }) => {
        const section = officer.user_manual[title];
        if (!section) return;
        
        Object.entries(section).forEach(([key, value]) => {
          rows.push({
            callsign: officer.callsign,
            email: officer.user_email,
            section: label,
            question: key.replace(/_/g, ' '),
            answer: value
          });
        });
      });

      // Add scoring section
      if (officer.user_manual.scoring) {
        Object.entries(officer.user_manual.scoring).forEach(([key, value]) => {
          rows.push({
            callsign: officer.callsign,
            email: officer.user_email,
            section: 'Scoring',
            question: key.replace(/_/g, ' '),
            answer: value
          });
        });
      }
    });
    
    return rows;
  };
  
  const data = flattenUserManuals();
  
  const exportToCSV = () => {
    const headers = ['Callsign', 'Email', 'Section', 'Question', 'Answer'];
    const rows = data.map(row => [
      row.callsign,
      row.email,
      row.section,
      row.question,
      `"${(row.answer || '').toString().replace(/"/g, '""')}"` // Escape quotes in CSV
    ]);
    
    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-manuals-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  
  if (data.length === 0) {
    return null;
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-4">
      <div className="flex items-center justify-between">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="p-0 hover:bg-transparent hover:text-slate-300 text-slate-400 flex items-center gap-2">
            <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            <h3 className="text-sm font-semibold uppercase tracking-wider">
              Officer User Manuals
            </h3>
          </Button>
        </CollapsibleTrigger>
        <Button
          onClick={exportToCSV}
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-700">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
      
      <CollapsibleContent>
        <div className="bg-slate-950 rounded-lg border border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900/50 border-b border-slate-800">
                <th className="px-4 py-3 text-left font-semibold text-slate-300">Callsign</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-300">Email</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-300">Section</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-300">Question</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-300">Answer</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr 
                  key={idx} 
                  className={`border-b border-slate-800/50 hover:bg-slate-900/30 transition-colors ${
                    idx % 2 === 0 ? 'bg-slate-950/50' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-slate-200 font-medium whitespace-nowrap">
                    {row.callsign}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {row.email}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">
                    <span className="bg-slate-800 px-2 py-1 rounded">{row.section}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 capitalize text-xs max-w-xs">
                    {row.question}
                  </td>
                  <td className="px-4 py-3 text-slate-300 max-w-lg">
                    <div className="line-clamp-3 text-xs">{row.answer}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}